import React, { ReactNode, useMemo } from 'react';
import { cva } from 'class-variance-authority';
import Tooltip from '@components/common/Tooltip';
import AmenityType from '@customTypes/amenity';
import { cn } from '@helpers/className';
import { getAmenityIcon } from './amenity-icon-mapping';

interface AmenityProps {
  amenity: AmenityType;
  display: 'compact' | 'expanded';
  size?: 'default' | 'medium' | 'small' | 'large';
}

const iconVariants = cva('', {
  variants: {
    size: {
      default: 'w-6 h-6',
      small: 'w-5 h-5',
      medium: 'w-6 h-6',
      large: 'w-7 h-7',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const textVariants = cva('whitespace-nowrap', {
  variants: {
    size: {
      default: 'text-xs d:text-sm',
      small: 'text-xs',
      medium: 'text-sm d:text-base',
      large: 'text-base d:text-lg',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const containerVariants = cva('inline-flex items-center', {
  variants: {
    size: {
      default: 'gap-1',
      small: 'gap-1',
      medium: 'gap-1',
      large: 'gap-4',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

function maybeWrapInTooltip(Icon: ReactNode, tooltipText: string, wrap: boolean) {
  if (wrap) {
    return (
      <Tooltip text={tooltipText} delay={1000}>
        {Icon}
      </Tooltip>
    );
  }
  return Icon;
}

function Amenity({ amenity, display = 'compact', size = 'default' }: AmenityProps) {
  const AmenityIcon = getAmenityIcon(amenity.name);
  const iconSize = iconVariants({ size });
  const textSize = textVariants({ size });
  const containerClassNames = containerVariants({ size });

  return (
    <span className={containerClassNames} aria-label={amenity.description}>
      {AmenityIcon &&
        maybeWrapInTooltip(
          <AmenityIcon className={iconSize} />,
          amenity.description,
          display === 'compact',
        )}
      {display === 'expanded' && <span className={textSize}>{amenity.description}</span>}
    </span>
  );
}

export interface AmenitiesProps {
  amenities: AmenityType[];
  variant?: 'compact' | 'expanded';
  direction?: 'horizontal' | 'vertical';
  size?: 'default' | 'small' | 'medium' | 'large';
  className?: string;
}

export default function Amenities({
  amenities,
  variant = 'compact',
  direction = 'horizontal',
  size = 'default',
  className,
}: AmenitiesProps) {
  const filteredAmenities = useMemo(
    () =>
      amenities.filter(
        (amenity) => getAmenityIcon(amenity.name) !== undefined && amenity.name !== 'restrooms',
      ),
    [amenities],
  );
  // Determine display mode based on variant

  const amenityElements = [];

  for (let i = 0, { length } = filteredAmenities; i < length; i += 1) {
    amenityElements.push(
      <Amenity key={i} amenity={filteredAmenities[i]} display={variant} size={size} />,
    );
    if (i < length - 1 && variant === 'expanded' && direction === 'horizontal') {
      amenityElements.push(
        <span
          key={`separator-${i}`}
          className="w-1 h-1 bg-gray-400 rounded-full self-center"
          aria-hidden="true"
        />,
      );
    }
  }

  const classNames = cn(
    'flex flex-wrap justify-start ',
    direction === 'vertical' ? 'flex-col h-full' : 'flex-row w-full items-center',
    variant === 'expanded' && 'gap-1',
    direction === 'vertical' && variant === 'compact' && 'gap-2',
    direction === 'horizontal' && variant === 'compact' && 'gap-1',
    className,
  );
  return <div className={classNames}>{amenityElements}</div>;
}
